import * as React from 'react';

import { clockPickerClasses, LocalizationProvider, TimePicker as MatTimePicker, TimePickerProps as MatTimePickerProps } from '@mui/x-date-pickers';

import { buttonBaseClasses, iconButtonClasses, inputAdornmentClasses, SxProps, TextField, Theme } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { archerConstants } from '../../utils';

export type TimePickValue = object | string | number | Date | null | undefined;
type MarginType = 'dense' | 'normal' | 'none';
export interface TimepickerProps {
  disabled?: boolean;
  fullWidth?: boolean;
  label?: string;
  locale?: string;
  margin?: MarginType;
  menuRef?: React.Ref<HTMLDivElement>;
  onChange: (date: unknown) => void;
  placeholder?: string;
  translate: any;
  value?: TimePickValue;
  variant?: 'dialog' | 'inline';
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  required?: boolean;
  sxPopper?: SxProps<Theme>;
  sxTextField?: SxProps<Theme>;
}

const styles: Record<string, SxProps<Theme>> = {
  timePickerRoot: {
    maxHeight: '3rem',
    '& > div': {
      maxHeight: '3rem'
    },
    '& > input': {
      maxHeight: '3rem'
    },
    [`& .${inputAdornmentClasses['positionEnd']} `]: {
      paddingRight: '3px',
      [`& .${iconButtonClasses['edgeEnd']}`]: {
        color: archerConstants.archerPrimary
      }
    }
  },
  timePickerDialogRoot: {
    [`& .${clockPickerClasses['root']}`]: {
      [`& .${clockPickerClasses['arrowSwitcher']}`]: {
        [`& .${iconButtonClasses['root']}:not(.${buttonBaseClasses['disabled']})`]: {
          color: 'text.secondary'
        }
      }
    },
    [`& .${buttonBaseClasses['root']}:active, & .${buttonBaseClasses['root']}:focus`]: {
      backgroundColor: archerConstants.archerPrimary
    }
  }
};

const ariaLabelPropertyName = 'aria-label';

const Timepicker = ({
  label,
  disabled,
  value,
  onChange,
  locale,
  fullWidth,
  margin,
  menuRef,
  error,
  helperText,
  placeholder,
  readOnly,
  required,
  sxPopper,
  sxTextField
}: TimepickerProps) => {
  moment.locale(locale);
  const { timePickerRoot, timePickerDialogRoot } = styles;
  const format = moment().localeData().longDateFormat('LT');
  const isTwentyFourHour = format !== 'HH:mm';

  const baseProps: MatTimePickerProps<any, any> = {
    ampm: isTwentyFourHour,
    label,
    disabled,
    value,
    onChange,
    readOnly,
    renderInput: (params: any) => (
      <TextField
        readonly={readOnly}
        variant="filled"
        sx={[timePickerRoot, sxTextField] as SxProps<Theme>}
        fullWidth={fullWidth}
        margin={margin}
        required={required}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...{ ...(params as any), [ariaLabelPropertyName]: label }}
      />
    )
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MatTimePicker
        {...{
          ...baseProps,
          PopperProps: {
            sx: [timePickerDialogRoot, sxPopper] as SxProps<Theme>,
            ref: menuRef
          }
        }}
      />
    </LocalizationProvider>
  );
};

Timepicker.defaultProps = {
  disabled: false,
  locale: 'en-US',
  margin: 'normal',
  readOnly: false,
  required: false
};

Timepicker.displayName = 'Timepicker';

export { Timepicker };
